import React, { useEffect, useMemo, useState } from 'react';
import { Radar, Send } from 'lucide-react';
import { apiClient } from '../../api/client';
import { logger } from '../../lib/logger';
import { useMyTracking } from '../../lib/useMyTracking';
import RadarCard, { stageStyle, stageText } from './RadarCard';
import RadarWatchCard from './RadarWatchCard';
import RadarLateCard from './RadarLateCard';
import { dirMeta } from './RadarPrep';

const RANK = { AC: 3, HAZIRLAN: 2, IZLE: 1 };
const setupRate = (r) => (r.radar.setup_history?.enough ? r.radar.setup_history.rate_pct : -1);
const byStrength = (a, b) =>
    RANK[b.radar.stage] - RANK[a.radar.stage] ||
    b.radar.passed_count - a.radar.passed_count ||
    setupRate(b) - setupRate(a) ||
    (b.early_signal?.score ?? 0) - (a.early_signal?.score ?? 0);
// İZLE: nearest-to-hard-evidence first (backend watch_rank = passed + nearest progress)
const byWatch = (a, b) =>
    (b.radar.watch_rank ?? 0) - (a.radar.watch_rank ?? 0) ||
    (b.radar.near_hard?.length ?? 0) - (a.radar.near_hard?.length ?? 0) ||
    (b.early_signal?.score ?? 0) - (a.early_signal?.score ?? 0);
const WATCH_CARDS = 6;

function useRadarStats() {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await apiClient.get('/radar/stats');
                if (!cancelled) setStats(data);
            } catch (e) {
                logger.warn('radar stats failed', e);
            }
        };
        load();
        const id = setInterval(load, 300000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, []);
    return stats;
}

const HistoryChip = ({ label, s }) => (
    <span className="text-[10px] text-slate-300">
        {label}:{' '}
        {s && s.n >= 5 && s.rate_pct !== null ? (
            <b className="font-mono text-slate-50">%{s.rate_pct}</b>
        ) : (
            <span className="text-slate-500">veri toplanıyor</span>
        )}
        <span className="text-slate-500"> ({s?.n || 0})</span>
    </span>
);

export default function RadarStrip({ rows, onSelect }) {
    const stats = useRadarStats();
    const { data: tracking } = useMyTracking();
    const trackedIds = useMemo(() => new Set((tracking?.items || []).filter((i) => i.status === 'open').map((i) => i.short_id)), [tracking]);
    const { main, watchCards, watchRest, watchTotal, late } = useMemo(() => {
        const picks = (rows || []).filter((r) => r.radar).sort(byStrength);
        const late = picks.filter((r) => r.radar.late).sort((a, b) => Math.abs(b.change_pct_24h || 0) - Math.abs(a.change_pct_24h || 0));
        const izle = picks.filter((r) => r.radar.stage === 'IZLE' && !r.radar.late).sort(byWatch);
        const watchCards = izle.filter((r) => r.radar.near_hard?.length).slice(0, WATCH_CARDS);
        const inCards = new Set(watchCards.map((r) => r.symbol));
        return {
            main: picks.filter((r) => r.radar.stage !== 'IZLE').slice(0, 9),
            watchCards,
            watchRest: izle.filter((r) => !inCards.has(r.symbol)).slice(0, 16),
            watchTotal: izle.length,
            late: late.slice(0, 6),
        };
    }, [rows]);
    const byStage = stats?.by_stage || {};

    return (
        <div data-testid="radar-strip" className="border border-sky-500/20 bg-slate-800 rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-700 flex items-center gap-x-3 gap-y-1.5 flex-wrap">
                <Radar className="h-4 w-4 text-sky-400 animate-pulse" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-sky-200 font-semibold">Erken Keşif Radarı · 7 Teyit</span>
                <span className="flex items-center gap-1.5 text-[10px]" data-testid="radar-stage-legend">
                    <span className="px-1.5 py-0.5 rounded-sm bg-sky-500/15 text-sky-200 border border-sky-500/40">İZLE 1-2</span>
                    <span className="text-slate-500">→</span>
                    <span className="px-1.5 py-0.5 rounded-sm bg-amber-400/20 text-amber-100 border border-amber-400/50" title="3+ teyit (geçmişte zayıf kalan teyit yarım sayılır) ve en az 1 sert kanıt (birikim/divergence, OI, hacim, kırılım)">HAZIRLAN 3+ · ≥1 sert kanıt</span>
                    <span className="text-slate-500">→</span>
                    <span className="px-1.5 py-0.5 rounded-sm bg-emerald-500 text-slate-950 font-bold">POZİSYON AÇ 4+/7 + kırılım + resmi sinyal</span>
                </span>
                <span className="ml-auto flex items-center gap-3 flex-wrap" data-testid="radar-history-summary" title={`24 saat içinde beklenen yönde ≥%${stats?.target_move_pct ?? 3} hareket (önce ters yönde %${stats?.adverse_move_pct ?? 3} gitmeden) = gerçekleşti`}>
                    <span className="text-[10px] text-slate-400">Geçmiş gerçekleşme (24s, ≥%3):</span>
                    <HistoryChip label="HAZIRLAN" s={byStage.HAZIRLAN} />
                    <HistoryChip label="AÇ" s={byStage.AC} />
                </span>
            </div>
            <div className="px-4 py-2 border-b border-slate-700/60 bg-slate-900/40 text-[10px] text-slate-400 flex items-center gap-1.5">
                <Send className="h-3 w-3 text-sky-400" />
                Bir coin POZİSYON AÇ aşamasına geçince Telegram'a #ID'li bildirim gider. HAZIRLAN'daki coinlerde henüz işlem açma.
            </div>
            {main.length === 0 && watchTotal === 0 && late.length === 0 ? (
                <div data-testid="radar-strip-empty" className="px-4 py-6 text-xs text-slate-400">
                    Şu an teyit toplayan coin yok — piyasa sakin. Radar her 25 saniyede yeniden tarar.
                </div>
            ) : (
                <div className="p-3 space-y-3">
                    {main.length > 0 ? (
                        <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 sm:grid sm:grid-cols-2 xl:grid-cols-3 sm:overflow-visible" data-testid="radar-main-cards">
                            {main.map((r) => (
                                <div key={r.symbol} onClick={() => onSelect?.(r.symbol)} className="cursor-pointer shrink-0 w-[86vw] snap-center sm:w-auto">
                                    <RadarCard row={r} history={byStage[r.radar.stage]} coinHistory={stats?.by_symbol?.[r.symbol]} trackedIds={trackedIds} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-[11px] text-slate-400">Henüz HAZIRLAN veya POZİSYON AÇ aşamasında coin yok.</div>
                    )}
                    {main.length > 1 && <div className="sm:hidden text-[10px] text-slate-500 -mt-1">{main.length} kart · yana kaydır →</div>}
                    {late.length > 0 && (
                        <div className="space-y-1.5" data-testid="radar-late-cards">
                            <div className="flex items-center gap-2 text-[10px] flex-wrap">
                                <span className="uppercase tracking-wider text-amber-200 font-semibold">Tren kaçtı · kovalama yapma</span>
                                <span className="text-slate-500">— teyitler var ama fiyat zaten koşmuş (24s ≥%10, 2×ATR ötesi veya RSI ≥75); HAZIRLAN sayılmaz, sadece geri çekilme planı</span>
                            </div>
                            <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1 sm:grid sm:grid-cols-2 xl:grid-cols-3 sm:overflow-visible">
                                {late.map((r) => (
                                    <div key={r.symbol} className="shrink-0 w-[80vw] snap-center sm:w-auto">
                                        <RadarLateCard row={r} onSelect={onSelect} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {watchCards.length > 0 && (
                        <div className="space-y-1.5" data-testid="radar-watch-cards">
                            <div className="flex items-center gap-2 text-[10px]">
                                <span className="uppercase tracking-wider text-sky-200 font-semibold">HAZIRLAN'a yaklaşanlar — eksik kanıt ne kadar yakın</span>
                                <span className="text-slate-500">— henüz sert kanıt yok (birikim/OI/hacim/kırılım); yüzde = eşiğe yakınlık. İşlem yok, aday listesi</span>
                            </div>
                            <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1 sm:grid sm:grid-cols-2 xl:grid-cols-3 sm:overflow-visible">
                                {watchCards.map((r) => (
                                    <div key={r.symbol} className="shrink-0 w-[80vw] snap-center sm:w-auto">
                                        <RadarWatchCard row={r} onSelect={onSelect} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {watchRest.length > 0 && (
                        <div className="flex items-center gap-1.5 flex-wrap" data-testid="radar-watch-list">
                            <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">Diğer izlemede ({watchTotal}):</span>
                            {watchRest.map((r) => {
                                const m = dirMeta(r.radar.direction);
                                return (
                                    <button
                                        key={r.symbol}
                                        onClick={() => onSelect?.(r.symbol)}
                                        data-testid={`radar-watch-${r.symbol}`}
                                        title={`${stageText(r.radar)} — ${r.radar.passed_count}/7 teyit. ${r.radar.action_tr}`}
                                        className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm hover:brightness-125 ${stageStyle('IZLE').badge}`}
                                    >
                                        {m && <span className={`font-bold mr-0.5 ${m.cls}`}>{m.arrow}</span>}
                                        {r.symbol.replace('USDT', '')} {r.radar.passed_count}/7
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
